import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Checkbox, Chip, Notice, Skeleton, StageTracker } from '@sauda/ui/kit';
import { RECORD_TYPES, type RecordType } from '@sauda/shared';
import { api } from '../../lib/api.js';
import type { Branch, Member } from '../../lib/types.js';

interface RecordCounts {
  properties: number;
  visits: number;
  deals: number;
  renewals: number;
  total: number;
}

/**
 * Removing a member (master 8.10, R14): their records are never deleted, so the open ones are
 * moved first, to one person or spread over a branch, and only then is the member deactivated.
 * Three steps: what they still have, where it goes, then a review.
 */
export function DeactivateWizard({
  member,
  members,
  branches,
  onClose,
  onDone,
}: {
  member: Member | null;
  members: Member[];
  branches: Branch[];
  onClose: () => void;
  onDone: (moved: number) => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<'member' | 'branch'>('member');
  const [toMember, setToMember] = useState('');
  const [toBranch, setToBranch] = useState('');
  const [types, setTypes] = useState<RecordType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const records = useQuery({
    queryKey: ['member-records', member?.id],
    queryFn: () => api<RecordCounts>(`/members/${member?.id ?? ''}/records`),
    enabled: member !== null,
  });
  const counts = records.data;
  const chosen = types ?? RECORD_TYPES.filter((type) => (counts?.[type] ?? 0) > 0);
  const moving = chosen.reduce((sum, type) => sum + (counts?.[type] ?? 0), 0);
  const needsTarget = (counts?.total ?? 0) > 0;
  const candidates = members.filter(
    (m) => m.status === 'active' && m.id !== member?.id && m.role !== 'accounts',
  );
  const targetName =
    mode === 'member'
      ? (candidates.find((m) => m.id === toMember)?.name ??
        candidates.find((m) => m.id === toMember)?.phone ??
        '')
      : (branches.find((b) => b.id === toBranch)?.name ?? '');
  const targetReady =
    !needsTarget || (chosen.length > 0 && (mode === 'member' ? toMember !== '' : toBranch !== ''));

  const run = useMutation({
    mutationFn: async () => {
      if (member === null) return 0;
      if (needsTarget) {
        await api(`/members/${member.id}/reassign`, {
          method: 'POST',
          body: {
            types: chosen,
            ...(mode === 'member' ? { toMemberId: toMember } : { toBranchId: toBranch }),
          },
        });
      }
      await api(`/members/${member.id}/deactivate`, { method: 'POST', body: {} });
      return moving;
    },
    onSuccess: (moved) => {
      setError(null);
      reset();
      onDone(moved);
    },
    onError: () => setError(t('team.reassignFailed')),
  });
  const reset = () => {
    setStep(0);
    setTypes(null);
    setToMember('');
    setToBranch('');
    setError(null);
  };
  const stages = [
    { key: 'has', label: t('team.stepHas') },
    { key: 'to', label: t('team.stepTo') },
    { key: 'review', label: t('team.stepReview') },
  ];
  const name = member?.name ?? member?.phone ?? '';
  const last = step === 2;
  return (
    <BottomSheet
      open={member !== null}
      onClose={() => {
        reset();
        onClose();
      }}
      label={t('team.removeTitle')}
      title={t('team.removeTitle')}
      closeLabel={t('app.close')}
      actions={
        <div className="step-bar">
          {step === 0 ? null : (
            <Button variant="text" size="lg" onClick={() => setStep(step - 1)}>
              {t('app.back')}
            </Button>
          )}
          {last ? (
            <Button size="lg" destructive loading={run.isPending} onClick={() => run.mutate()}>
              {needsTarget ? t('team.moveAndRemove') : t('team.remove')}
            </Button>
          ) : (
            <Button
              size="lg"
              disabled={records.isPending || (step === 1 && !targetReady)}
              onClick={() => setStep(step === 0 && !needsTarget ? 2 : step + 1)}
            >
              {t('property.next')}
            </Button>
          )}
        </div>
      }
    >
      <div className="form-stack">
        <StageTracker stages={stages} current={step} label={t('team.removeSteps')} />
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {records.isPending ? <Skeleton height={72} /> : null}
        {step === 0 && counts !== undefined ? (
          <>
            <h3 className="k-t-title-sm">{t('team.stillHas', { name })}</h3>
            {counts.total === 0 ? <p className="k-t-body">{t('team.noRecords')}</p> : null}
            {counts.total > 0 ? (
              <p className="k-t-body" data-testid="open-records">
                {t('team.hasRecords', { count: counts.total })}
              </p>
            ) : null}
            {RECORD_TYPES.filter((type) => counts[type] > 0).map((type) => (
              <Checkbox
                key={type}
                label={t(`team.records_${type}`, { count: counts[type] })}
                checked={chosen.includes(type)}
                onChange={(e) =>
                  setTypes(e.target.checked ? [...chosen, type] : chosen.filter((c) => c !== type))
                }
              />
            ))}
          </>
        ) : null}
        {step === 1 ? (
          <>
            <div className="chips-row" role="group" aria-label={t('team.moveHow')}>
              <Chip selected={mode === 'member'} onClick={() => setMode('member')}>
                {t('team.toOnePerson')}
              </Chip>
              <Chip selected={mode === 'branch'} onClick={() => setMode('branch')}>
                {t('team.spreadBranch')}
              </Chip>
            </div>
            <div className="chips-row" role="group" aria-label={t('team.moveTo')}>
              {mode === 'member'
                ? candidates.map((m) => (
                    <Chip key={m.id} selected={toMember === m.id} onClick={() => setToMember(m.id)}>
                      {m.name ?? m.phone}
                    </Chip>
                  ))
                : branches.map((b) => (
                    <Chip key={b.id} selected={toBranch === b.id} onClick={() => setToBranch(b.id)}>
                      {b.name}
                    </Chip>
                  ))}
            </div>
          </>
        ) : null}
        {last ? (
          <p className="k-t-body-lg">
            {needsTarget
              ? t('team.review', { count: moving, to: targetName, name })
              : t('team.reviewEmpty', { name })}
          </p>
        ) : null}
      </div>
    </BottomSheet>
  );
}
